import { useMask } from '@react-input/mask';
import React, { useEffect, useRef, useState } from 'react';

import SvgError from '@sats-group/icons/16/error';
import HiddenInput from '@sats-group/ui-lib/react/hidden-input';
import Select from '@sats-group/ui-lib/react/select';
import Text from '@sats-group/ui-lib/react/text';
import TextInput from '@sats-group/ui-lib/react/text-input';

import PhoneInput from 'shared/components/phone-input/phone-input';
import Spinner from 'shared/components/spinner/spinner';
import StartDate from 'shared/components/start-date/start-date';
import { replaceQueryParameters } from 'shared/utils/query-strings';

import { get } from 'helpers/api-helper';

import type { FormInputs as Props } from './form-inputs.types';

const FormInputs: React.FC<Props> = ({
  address,
  birthDay,
  companyName,
  email,
  firstName,
  gender,
  genderIdentity,
  lastName,
  phone,
  postalAreaEndpoint,
  postalAreaName,
  postalCode,
  startDate,
  ssn,
}) => {
  const inputRef = useMask({
    mask: 'dd-mm-åååå', // NOTE: Birthday is only used in Norway.
    replacement: { d: /\d/, m: /\d/, å: /\d/ },
    showMask: true,
  });

  const [isLoading, setIsLoading] = useState(false);

  const [postalArea, setPostalArea] = useState('');

  const [postalAreaErrorMessage, setPostalAreaErrorMessage] = useState('');

  const postalCodeValue = useRef('');

  // Keeps track of the debounce id between re-renders, to avoid starting multiple timers.
  const debounceTimerRef = useRef<number | null>(null);

  const handlePostalCodeChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    const value = event.target.value;

    setPostalArea('');
    setPostalAreaErrorMessage('');

    // NOTE: We don't care about postal codes shorter than 4 digits, as Norway, Denmark, Sweden and Finland are all 4+
    if (value.length < 4) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    postalCodeValue.current = value;

    debounceTimerRef.current = window.setTimeout(() => {
      get<{ postalArea: string; errorMessage?: string }>(
        replaceQueryParameters(postalAreaEndpoint, { code: value })
      )
        .then(data => {
          if (postalCodeValue.current === value) {
            if (data.errorMessage) {
              setPostalAreaErrorMessage(data.errorMessage);
            } else {
              setPostalArea(data.postalArea);
            }
          }
          setIsLoading(false);
        })
        .catch(() => {
          setIsLoading(false);
        });
    }, 3000);
  };

  // Cleans up the timer on component unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return (
    <div className="form-inputs">
      {companyName ? <TextInput {...companyName} /> : null}
      <TextInput {...firstName} />
      <TextInput {...lastName} />
      {birthDay ? <TextInput {...birthDay} ref={inputRef} /> : null}
      {ssn ? <TextInput {...ssn} /> : null}
      {gender ? <Select wide={true} {...gender} /> : null}
      {genderIdentity ? <Select wide={true} {...genderIdentity} /> : null}
      <PhoneInput {...phone} />
      <TextInput {...email} />
      <TextInput {...address} />
      <div className="form-inputs__postal-inputs">
        <div>
          <TextInput
            {...postalCode}
            onChange={handlePostalCodeChange}
            hasError={!!postalAreaErrorMessage || postalCode.hasError}
            aria-describedby="postal-area-error"
          />
          {postalAreaErrorMessage ? (
            <Text
              className="form-inputs__postal-area-error-message"
              size={'interface'}
              id="postal-area-error"
            >
              <div>
                <SvgError />
              </div>
              {postalAreaErrorMessage}
            </Text>
          ) : null}
        </div>
        <HiddenInput value={postalArea} name={postalAreaName} />
        <div className="form-inputs__postal-area">
          {isLoading ? <Spinner size={Spinner.sizes.mini} /> : null}
          {postalArea ? <Text>{postalArea}</Text> : null}
        </div>
      </div>

      {startDate ? (
        <div>
          <StartDate {...startDate} />
        </div>
      ) : null}
    </div>
  );
};

export default FormInputs;
